<template>
  <div class="filter-select position-relative d-flex flex-column w-100">
    <eg-input
      v-if="settings.isSearch"
      v-model="searchValue"
      :placeholder="placeholder"
      size="small"
      :debounce="300"
      :show-validation="false"
      variant="search"
      class="filter-select-input"
    />
    <div class="filter-select-items d-flex overflow-auto">
      <template
        v-if="settings.selectAll && settings.many && filteredOptions.length > 0"
      >
        <eg-checkbox-input
          vertical
          :name="`filter-${_uid}`"
          :options="[{ label: 'Select All', value: true }]"
          :value="isAllSelected"
          :indeterminate="indeterminate"
          @change="selectAllItems"
        />
      </template>

      <component
        :is="settings.many ? 'eg-checkbox-input' : 'eg-radio-input'"
        v-if="items.length > 0 && filteredOptions.length > 0"
        :name="`filter-${_uid}`"
        :value="value"
        :options="filteredOptions"
        :type="settings.many ? 'checkbox' : 'radio'"
        vertical
        :value-is-id="valueIsId"
        @change="selectItems"
      />
      <div
        v-if="items.length > 0 && filteredOptions.length === 0"
        class="p-3 text-center"
      >
        No items found, please try again with different search query
      </div>
      <div v-if="items.length === 0" class="p-3 text-center">
        Sorry, you have no items to select from
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number, Object, Array, Boolean],
      default: "",
    },
    valueKey: {
      type: String,
      default: "id",
    },
    size: {
      type: String,
      default: "md",
    },
    placeholder: {
      type: String,
      default: "Search items...",
    },
    valueIsId: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      searchValue: "",
      indeterminate: false,
      isAllSelected: false,
    };
  },

  computed: {
    options() {
      return this.items.map((item) => ({
        id: item.id,
        label: item.name,
        value: item[this.valueKey],
      }));
    },

    filteredOptions() {
      if (!this.searchValue) {
        return this.options;
      }

      return this.options.filter((item) =>
        item.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.updateSelectionState(newValue);
      },
    },
    items: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.updateSelectionState(this.value);
        });
      },
    },
  },

  methods: {
    updateSelectionState(selectedValues) {
      if (!this.settings.many || !Array.isArray(selectedValues)) {
        this.isAllSelected = false;
        this.indeterminate = false;
        return;
      }

      const selectedCount = selectedValues.length;
      const totalCount = this.items.length;
      this.isAllSelected = selectedCount === totalCount && totalCount > 0;
      this.indeterminate = selectedCount > 0 && selectedCount < totalCount;
    },

    selectItems(itemIds) {
      const items = this.items.filter((item) => {
        const normalizedValue = String(item[this.valueKey] ?? "null");
        return Array.isArray(itemIds)
          ? itemIds.includes(normalizedValue)
          : itemIds === normalizedValue;
      });

      if (items.length === 0) {
        this.indeterminate = false;
        this.isAllSelected = false;
      } else if (items.length > 0 && items.length < this.items.length) {
        this.indeterminate = true;
      } else {
        this.indeterminate = false;
        this.isAllSelected = true;
      }

      this.$emit("input", this.settings.many ? items : items[0]);
    },

    selectAllItems([checked]) {
      this.isAllSelected = checked;
      this.indeterminate = false;
      const selectedItems = checked
        ? this.filteredOptions.map((option) => String(option.id))
        : [];
      this.selectItems(selectedItems);
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-select-input {
  padding: 6px 9px;
  border-bottom: 1px solid var(--color-stroke-neutral-default);

  &.eg-input_variant-search::v-deep {
    &.is-filled:focus-within .eg-input__control,
    &:focus-within .eg-input__control {
      box-shadow: none;
    }

    .eg-input__prefix {
      padding-left: 0;
      color: var(--color-icon-neutral-disabled, #8f9ca9);

      .eg-icon {
        font-size: 20px;
      }
    }

    .eg-input__inner {
      padding-left: 8px;
    }

    .eg-input__control {
      border: none;
    }
  }
}

.filter-select-items {
  display: flex;
  flex-direction: column;

  .eg-radio-checkbox-input {
    gap: 0;
    width: 100%;
  }

  &::v-deep {
    .eg-radio-checkbox-input__label {
      margin: 0;
      padding: 8px 12px;
      line-height: 24px;

      &:hover {
        background-color: var(--color-surface-neutral-press);
      }
    }

    .eg-radio-checkbox-input__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
